import React, { useEffect, useState } from 'react';
import { onAuthStateChanged, type User } from '@firebase/auth';
import { auth } from '../firebase';
import { Navigate } from 'react-router-dom';
import { getUserMe } from '../utils/secureFunctions';

interface Props {
  children: React.ReactNode;
  roles?: Array<'admin' | 'moderator' | 'super_admin'>; // optional role gating
}

export default function ProtectedRoute({ children, roles }: Props): React.ReactElement | null {
  const [user, setUser] = useState<User | null>(() => auth.currentUser);
  const [roleAllowed, setRoleAllowed] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u && roles && roles.length) {
        try {
          const token = await u.getIdTokenResult();
          const claims: any = token?.claims || {};
          const role = String(claims.role || '').toLowerCase();

          const userRoles: string[] = [];
          if (claims.admin === true || role === 'admin' || role === 'super_admin') userRoles.push('admin');
          if (role === 'moderator' || claims.moderator === true) userRoles.push('moderator');
          if (role === 'super_admin' || claims.super_admin === true) userRoles.push('super_admin');

          if (!roles.some((r) => userRoles.includes(r))) {
            // Roles are stored server-side; token claims are only a fast path.
            const me = await getUserMe().catch(() => null);
            const dbRole = String((me?.profile as { role?: string } | undefined)?.role || '').toLowerCase();
            if (dbRole) userRoles.push(dbRole);
          }
          setRoleAllowed(roles.some((r) => userRoles.includes(r)));
        } catch {
          setRoleAllowed(false);
        }
      } else if (roles && roles.length) {
        setRoleAllowed(false);
      } else {
        setRoleAllowed(true);
      }
    });
    return () => unsub();
  }, [roles]);

  if (!user) return <Navigate to="/login" replace />;
  if (roles && roles.length) {
    if (roleAllowed === undefined) return null; // still resolving
    if (!roleAllowed) return <Navigate to="/home" replace />;
  }
  return <>{children}</>;
}
